import { useState, useEffect, useRef } from 'react';
import { 
  Search, X, MapPin, Calendar, Users, DollarSign, Globe, Plane,
  Sun, Heart, Clock, Compass
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { SearchFilterPanel, ActiveFilterTags } from './SmartSearchFilters';
import SearchSuggestionsDropdown from './SearchSuggestionsDropdown';

const SmartSearch = ({ packages = [], themeTours = [], onSearch, className = '', searchType = 'all' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilters, setActiveFilters] = useState({
    destination: '',
    country: '',
    category: '',
    duration: null,
    priceRange: [0, 500000],
    type: '',
    theme: '',
  });
  const [suggestions, setSuggestions] = useState([]);
  const [recentSearches, setRecentSearches] = useState([]);
  const inputRef = useRef(null);

  // Combine packages and theme tours for unified search
  const allItems = [...packages, ...themeTours];
  
  // Extract unique values from all items for suggestions
  const allCountries = [...new Set(allItems.map(p => p.country).filter(Boolean))];
  const allCities = [...new Set(allItems.flatMap(p => [p.city, p.destination]).filter(Boolean))];
  const allTypes = [...new Set(packages.map(p => p.type).filter(Boolean))];
  const allThemes = [...new Set(themeTours.map(t => t.theme).filter(Boolean))];
  const durations = [...new Set(allItems.map(p => p.duration).filter(Boolean))].sort((a, b) => 
    parseInt(a) - parseInt(b)
  );

  // Popular searches - include both package types and theme tours
  const popularSearches = [
    { label: 'Honeymoon', icon: Heart, type: 'type', value: 'honeymoon' },
    { label: 'Family Tours', icon: Users, type: 'type', value: 'family' },
    { label: 'International', icon: Globe, type: 'category', value: 'international' },
    { label: 'Domestic', icon: MapPin, type: 'category', value: 'domestic' },
    { label: 'Weekend Getaway', icon: Sun, type: 'duration', value: '3' },
    { label: 'Week Long', icon: Calendar, type: 'duration', value: '7' },
    ...(allThemes.length > 0 ? [
      { label: 'Wildlife', icon: Globe, type: 'theme', value: 'wildlife' },
      { label: 'Adventure', icon: Plane, type: 'theme', value: 'adventure' },
    ] : [])
  ];

  // Load recent searches from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('recentPackageSearches');
    if (saved) {
      setRecentSearches(JSON.parse(saved).slice(0, 5));
    }
  }, []);

  // Generate smart suggestions based on query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSuggestions([]);
      return;
    }

    const query = searchQuery.toLowerCase();
    const newSuggestions = [];

    // Check for duration patterns
    const durationMatch = query.match(/(\d+)\s*(night|day|days|nights)/i);
    if (durationMatch) {
      const num = parseInt(durationMatch[1]);
      newSuggestions.push({
        type: 'duration',
        label: `${num} Nights packages`,
        icon: Clock,
        value: num,
        highlight: true
      });
    }

    // Check for price patterns
    const priceMatch = query.match(/under\s*(\d+)|below\s*(\d+)|(\d+)k?/i);
    if (priceMatch) {
      const price = parseInt(priceMatch[1] || priceMatch[2] || priceMatch[3]);
      const actualPrice = price < 1000 ? price * 1000 : price;
      newSuggestions.push({
        type: 'price',
        label: `Packages under ₹${actualPrice.toLocaleString()}`,
        icon: DollarSign,
        value: actualPrice,
        highlight: true
      });
    }

    // Match countries
    allCountries.forEach(country => {
      if (country.toLowerCase().includes(query)) {
        newSuggestions.push({
          type: 'country',
          label: country,
          icon: Globe,
          value: country
        });
      }
    });

    // Match cities/destinations
    allCities.forEach(city => {
      if (city && city.toLowerCase().includes(query) && !newSuggestions.find(s => s.label === city)) {
        newSuggestions.push({
          type: 'city',
          label: city,
          icon: MapPin,
          value: city
        });
      }
    });

    // Match types
    allTypes.forEach(type => {
      if (type && type.toLowerCase().includes(query)) {
        newSuggestions.push({
          type: 'type',
          label: `${type.charAt(0).toUpperCase() + type.slice(1)} Tours`,
          icon: type === 'honeymoon' ? Heart : type === 'family' ? Users : Plane,
          value: type
        });
      }
    });

    // Match themes (for theme tours)
    allThemes.forEach(theme => {
      if (theme && theme.toLowerCase().includes(query)) {
        newSuggestions.push({
          type: 'theme',
          label: `${theme.charAt(0).toUpperCase() + theme.slice(1)} Theme`,
          icon: Compass,
          value: theme
        });
      }
    });

    // Check for category keywords
    if ('international'.includes(query) || 'abroad'.includes(query) || 'foreign'.includes(query)) {
      newSuggestions.push({
        type: 'category',
        label: 'International Packages',
        icon: Plane,
        value: 'international'
      });
    }
    if ('domestic'.includes(query) || 'india'.includes(query) || 'indian'.includes(query)) {
      newSuggestions.push({
        type: 'category',
        label: 'Domestic Packages',
        icon: MapPin,
        value: 'domestic'
      });
    }

    setSuggestions(newSuggestions.slice(0, 8));
  }, [searchQuery, packages, themeTours]);

  // Save search to recent
  const saveToRecent = (search) => {
    const updated = [search, ...recentSearches.filter(s => s.label !== search.label)].slice(0, 5);
    setRecentSearches(updated);
    localStorage.setItem('recentPackageSearches', JSON.stringify(updated));
  };

  // Apply search/filter
  const applySearch = (suggestion) => {
    saveToRecent(suggestion);
    
    const newFilters = { ...activeFilters };
    
    switch (suggestion.type) {
      case 'country':
        newFilters.country = suggestion.value;
        break;
      case 'city':
        newFilters.destination = suggestion.value;
        break;
      case 'category':
        newFilters.category = suggestion.value;
        break;
      case 'type':
        newFilters.type = suggestion.value;
        break;
      case 'theme':
        newFilters.theme = suggestion.value;
        break;
      case 'duration':
        newFilters.duration = suggestion.value;
        break;
      case 'price':
        newFilters.priceRange = [0, suggestion.value];
        break;
      default:
        break;
    }
    
    setActiveFilters(newFilters);
    setSearchQuery(suggestion.label);
    setIsOpen(false);
    
    // Filter packages - pass suggestion.label directly since state update is async
    filterPackages(newFilters, suggestion.label);
  };

  // Main filter function - handles both packages and theme tours
  const filterPackages = (filters, query = '') => {
    let filteredPackages = [...packages];
    let filteredThemeTours = [...themeTours];
    
    // Text search — always apply when there's a query
    if (query) {
      const q = query.toLowerCase();
      filteredPackages = filteredPackages.filter(p => 
        p.title?.toLowerCase().includes(q) ||
        p.destination?.toLowerCase().includes(q) ||
        p.country?.toLowerCase().includes(q) ||
        p.city?.toLowerCase().includes(q) ||
        p.description?.toLowerCase().includes(q) ||
        p.highlights?.some(h => h.toLowerCase().includes(q)) ||
        p.type?.toLowerCase().includes(q) ||
        p.types?.some(t => t.toLowerCase().includes(q))
      );
      filteredThemeTours = filteredThemeTours.filter(t => 
        t.title?.toLowerCase().includes(q) ||
        t.destination?.toLowerCase().includes(q) ||
        t.country?.toLowerCase().includes(q) ||
        t.city?.toLowerCase().includes(q) ||
        t.theme?.toLowerCase().includes(q) ||
        t.description?.toLowerCase().includes(q) ||
        t.highlights?.some(h => h.toLowerCase().includes(q))
      );
    }
    
    // Apply filters to packages
    if (filters.country) {
      filteredPackages = filteredPackages.filter(p => p.country === filters.country);
      filteredThemeTours = filteredThemeTours.filter(t => t.country === filters.country);
    }
    if (filters.destination) {
      filteredPackages = filteredPackages.filter(p => 
        p.destination === filters.destination || 
        p.city === filters.destination
      );
      filteredThemeTours = filteredThemeTours.filter(t => 
        t.destination === filters.destination || 
        t.city === filters.destination
      );
    }
    if (filters.category) {
      filteredPackages = filteredPackages.filter(p => p.category === filters.category);
      filteredThemeTours = filteredThemeTours.filter(t => t.category === filters.category);
    }
    if (filters.type) {
      filteredPackages = filteredPackages.filter(p => 
        p.type === filters.type || (p.types && p.types.includes(filters.type))
      );
    }
    if (filters.theme) {
      filteredThemeTours = filteredThemeTours.filter(t => t.theme === filters.theme);
    }
    if (filters.duration) {
      const filterNights = parseInt(filters.duration);
      filteredPackages = filteredPackages.filter(p => {
        const nights = parseInt(p.duration);
        return nights === filterNights || nights === filterNights - 1 || nights === filterNights + 1;
      });
      filteredThemeTours = filteredThemeTours.filter(t => {
        const nights = parseInt(t.duration);
        return nights === filterNights || nights === filterNights - 1 || nights === filterNights + 1;
      });
    }
    if (filters.priceRange && filters.priceRange[1] < 500000) {
      filteredPackages = filteredPackages.filter(p => p.price <= filters.priceRange[1]);
      filteredThemeTours = filteredThemeTours.filter(t => t.price <= filters.priceRange[1]);
    }
    
    // Return both filtered arrays
    onSearch(filteredPackages, filters, filteredThemeTours);
  };

  // Clear all filters
  const clearFilters = () => {
    const emptyFilters = {
      destination: '',
      country: '',
      category: '',
      duration: null,
      priceRange: [0, 500000],
      type: '',
      theme: '',
    };
    setActiveFilters(emptyFilters);
    setSearchQuery('');
    onSearch(packages, emptyFilters, themeTours);
  };

  // Handle Enter key
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      if (suggestions.length > 0) {
        applySearch(suggestions[0]);
      } else {
        filterPackages(activeFilters, searchQuery);
        setIsOpen(false);
      }
    }
  };

  // Count active filters
  const activeFilterCount = Object.values(activeFilters).filter(v => 
    v && (Array.isArray(v) ? v[1] < 500000 : true)
  ).length;

  return (
    <div className={`relative ${className}`}>
      {/* Main Search Bar */}
      <div className="relative">
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-white/70" />
            <Input
              ref={inputRef}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => setIsOpen(true)}
              onBlur={(e) => {
                // Close dropdown after a short delay to allow clicking on suggestions
                // Don't close if clicking within the search container
                setTimeout(() => {
                  if (!e.currentTarget?.contains(document.activeElement)) {
                    setIsOpen(false);
                  }
                }, 200);
              }}
              onKeyDown={handleKeyDown}
              placeholder="Search destinations, countries, durations... (e.g., 'Dubai', '5 nights', 'honeymoon')"
              className="pl-12 pr-12 h-14 text-lg rounded-full border-2 border-white/30 bg-white/10 backdrop-blur-sm text-white placeholder:text-white/70 focus:border-white focus:bg-white/20 shadow-lg"
              data-testid="smart-search-input"
            />
            {searchQuery && (
              <button
                onClick={() => { setSearchQuery(''); clearFilters(); }}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white"
              >
                <X className="h-5 w-5" />
              </button>
            )}
          </div>
          
          {/* Filter Button */}
          <SearchFilterPanel
            activeFilters={activeFilters}
            setActiveFilters={setActiveFilters}
            filterPackages={filterPackages}
            searchQuery={searchQuery}
            clearFilters={clearFilters}
            activeFilterCount={activeFilterCount}
          />
        </div>

        {/* Active Filter Tags */}
        {activeFilterCount > 0 && (
          <ActiveFilterTags
            activeFilters={activeFilters}
            setActiveFilters={setActiveFilters}
            filterPackages={filterPackages}
            searchQuery={searchQuery}
          />
        )}
      </div>

      {/* Suggestions Dropdown */}
      {isOpen && (
        <SearchSuggestionsDropdown
          suggestions={suggestions}
          searchQuery={searchQuery}
          popularSearches={popularSearches}
          recentSearches={recentSearches}
          applySearch={applySearch}
        />
      )}

      {/* Backdrop - closes dropdown when clicking outside, positioned behind search bar */}
      {isOpen && (
        <div 
          className="fixed inset-0 -z-10" 
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};

export default SmartSearch;
